/**
 * The pure part of the ONE candidate job evaluation (see candidateJobEvaluation.ts for the rules):
 * no Firebase imports, so scripts/syncFunctionsGenerated.mjs copies it into functions/src/generated/
 * and the employer's application view (getCandidateJobEvaluation, functions/src/recruiterSignals.ts)
 * runs exactly the code the candidate's own job page runs, on the same full private profile. Before
 * 2026-10-08 the employer view scored a trimmed public copy and could differ by a point or more.
 */
import type { Job } from '../firestore/types';
import type { JobMatchScore, ReadinessBreakdown, ReadinessProfile } from '../../types/readiness';
import { calculateJobMatch, type JobMatchOptions } from '../readiness/calculateJobMatch';
import { calculateReadiness } from '../readiness/calculateReadiness';
import { toReadinessProfile } from '../readiness/profileAdapter';
import { candidateMatchLabel, type CandidateMatchLabel } from '../readiness/candidateMatchLabel';
import { describeDocumentReviewGap, getDocumentReviewGate, type DocumentReviewGap, type DocumentReviewGate } from '../credentials/documentReviewGate';

export type CandidateJobEvaluation = {
  job: Job;
  match: JobMatchScore | null;
  gate: DocumentReviewGate | null;
  /** What every surface prints for the match. */
  label: CandidateMatchLabel | null;
  /** A REQUIRED certification is not on the profile. Binary: no percentage is shown. */
  hardGateFailed: boolean;
  /** Required credential held, but its document has not passed review. */
  documentsBlocked: boolean;
  /** Required certifications not on the profile (display names). */
  missingOnProfile: string[];
  /** Required certifications held whose document is still pending / rejected / not uploaded. */
  pendingReview: Array<{ certification: string; reason: string }>;
  /** True when nothing blocks applying (deadline and "already applied" are the caller's concern). */
  canApply: boolean;
  /**
   * The job's employer accepts applications from people missing a required credential, and this
   * candidate is missing one: they may apply now and rank below complete applicants until it is
   * reviewed (canApply is true in that case).
   */
  applyAnyway: boolean;
};

export function evaluateJobForCandidate(params: {
  profile: unknown;
  readinessProfile: ReadinessProfile | null;
  readiness: ReadinessBreakdown | null;
  job: Job;
  /** Match options (the driver_roles flag, and "now" for the counting rule). Omitted = the current time. */
  options?: JobMatchOptions;
}): CandidateJobEvaluation {
  const { profile, readinessProfile, readiness, job } = params;
  const options = params.options ?? {};
  const match = readinessProfile && readiness ? calculateJobMatch(readinessProfile, job, readiness, options) : null;
  const gate = profile ? getDocumentReviewGate({ profile, job, now: options.now }) : null;
  const gaps: DocumentReviewGap[] = gate && !gate.eligible ? gate.gaps : [];
  // The gate is the one source for WHY a required item is not met (not on the profile vs on it but
  // pending, rejected or expired). The score treats all of these as not met (counting rule), so
  // match.missing also lists held-but-pending items; they belong in pendingReview, not here.
  const missingOnProfile = gate
    ? [...new Set(gaps.filter((g) => g.reason === 'not_on_profile').map((g) => g.certification).filter((c): c is string => Boolean(c)))]
    : [...new Set(match?.pillars.certifications.missing ?? [])];
  const pendingReview = gaps
    .filter((g) => g.reason !== 'not_on_profile')
    .map((g) => ({ certification: g.certification || 'A credential', reason: describeDocumentReviewGap(g) }));
  // A required item NOT ON THE PROFILE. (One on the profile but pending, rejected or expired is
  // documentsBlocked; the score caps both the same way.)
  const hardGateFailed = missingOnProfile.length > 0;
  const documentsBlocked = Boolean(gate && !gate.eligible);
  const label = candidateMatchLabel(match ? match.total : null, {
    documentsBlocked,
    hardGateFailed,
    missingRequired: missingOnProfile,
    notReviewed: pendingReview.map((p) => p.certification),
  });
  // Same switch applyToJob reads from the job document (src/lib/applications/missingCredentials.ts).
  const applyAnyway = (hardGateFailed || documentsBlocked) && (job as { acceptMissingCredentials?: unknown }).acceptMissingCredentials === true;
  return {
    job,
    match,
    gate,
    label,
    hardGateFailed,
    documentsBlocked,
    missingOnProfile,
    pendingReview,
    canApply: (!hardGateFailed && !documentsBlocked) || applyAnyway,
    applyAnyway,
  };
}

/** Convenience for surfaces that hold only the private profile. */
export function evaluateJobsForCandidate(profile: unknown, jobs: Job[], now: Date = new Date(), options: JobMatchOptions = {}): CandidateJobEvaluation[] {
  const readinessProfile = toReadinessProfile(profile as Parameters<typeof toReadinessProfile>[0]);
  const readiness = readinessProfile ? calculateReadiness(readinessProfile, now) : null;
  return jobs.map((job) => evaluateJobForCandidate({ profile, readinessProfile, readiness, job, options: { ...options, now } }));
}

/** Rank: eligible-with-score first (by score), then document-pending, then not eligible. */
export function compareCandidateJobEvaluations(a: CandidateJobEvaluation, b: CandidateJobEvaluation): number {
  const tier = (e: CandidateJobEvaluation) => (e.hardGateFailed ? 2 : e.documentsBlocked ? 1 : 0);
  const t = tier(a) - tier(b);
  if (t !== 0) return t;
  return (b.match?.total ?? 0) - (a.match?.total ?? 0);
}
